import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UserService } from '../../layout/service/user.service';

@Component({
    selector: 'app-show-article',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        CardModule,
        ToastModule
    ],
    providers: [MessageService],
    template: `
        <div class="min-h-screen bg-gray-50 p-4 lg:p-8">
            <div class="max-w-4xl mx-auto">
                <!-- Back Button -->
                <button 
                    (click)="goBack()"
                    class="mb-6 flex items-center text-gray-600 hover:text-blue-600 transition-colors bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                    <i class="pi pi-arrow-left mr-2"></i>
                    Kembali ke Daftar
                </button>

                <!-- Loading State -->
                <div *ngIf="loading" class="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <i class="pi pi-spin pi-spinner text-4xl text-blue-600 mb-4"></i>
                    <p class="text-gray-600">Memuat artikel...</p>
                </div>

                <!-- Error State -->
                <div *ngIf="error" class="bg-white rounded-xl shadow-sm border border-red-200 p-12 text-center">
                    <i class="pi pi-exclamation-circle text-5xl text-red-500 mb-4 block"></i>
                    <h2 class="text-2xl font-bold text-gray-900 mb-2">Artikel Tidak Ditemukan</h2>
                    <p class="text-gray-600 mb-6">{{ error }}</p>
                    <p-button label="Kembali" icon="pi pi-arrow-left" (onClick)="goBack()"></p-button>
                </div>

                <!-- Content -->
                <div *ngIf="!loading && !error && article" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <!-- Hero Cover Image -->
                    <div class="w-full h-64 md:h-96 bg-gray-100 relative">
                        <img *ngIf="article.gambar" [src]="getImageUrl(article.gambar)" [alt]="article.judul" class="w-full h-full object-cover">
                        <div *ngIf="!article.gambar" class="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                            <i class="pi pi-file" style="font-size: 5rem;color:white;"></i>
                        </div>
                    </div>

                    <div class="p-6 md:p-10">
                        <!-- Metadata -->
                        <div class="flex flex-wrap items-center gap-3 md:gap-6 text-sm text-gray-600 mb-6 pb-6 border-b border-gray-100">
                            <span class="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                                {{ article.kategori }}
                            </span>
                            <div class="flex items-center" *ngIf="article.tanggal">
                                <i class="pi pi-calendar mr-2"></i>
                                {{ formatDate(article.tanggal) }}
                            </div>
                            <div class="flex items-center" *ngIf="article.author">
                                <i class="pi pi-user mr-2"></i>
                                {{ article.author }}
                            </div>
                            <div class="flex items-center" *ngIf="article.read_time">
                                <i class="pi pi-clock mr-2"></i>
                                {{ article.read_time }}
                            </div>
                        </div>

                        <!-- Title -->
                        <h1 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                            {{ article.judul }}
                        </h1>

                        <!-- Excerpt -->
                        <div class="bg-gray-50 border-l-4 border-blue-500 p-4 mb-8 text-gray-700 italic">
                            {{ article.excerpt }}
                        </div>

                        <!-- Main Content -->
                        <!-- If content is HTML, use innerHTML. Otherwise standard render. -->
                        <div class="prose prose-lg max-w-none text-gray-800 leading-relaxed mb-10 whitespace-pre-line" [innerHTML]="formatContent(article.konten)">
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <p-toast></p-toast>
    `
})
export class ShowArticle implements OnInit {
    article: any = null;
    loading = true;
    error: string | null = null;
    isAuthorizedUser = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private messageService: MessageService,
        private sanitizer: DomSanitizer
    ) { }

    ngOnInit() {
        this.checkUserAuthorization();
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            this.loadArticle(id);
        } else {
            this.error = 'ID Artikel tidak valid';
            this.loading = false;
        }
    }

    checkUserAuthorization() {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                const user = JSON.parse(userData);
                this.isAuthorizedUser = user.ID_ACCESS_ROLE === 'N2FWBVCZUE' || user.ID_ACCESS_ROLE === '4AEJ1KAJTY';
            }
        } catch (e) {
            this.isAuthorizedUser = false;
        }
    }

    loadArticle(id: string) {
        this.loading = true;
        this.error = null;

        this.userService.getArticles('', id).then(
            (response) => {
                const articles = response.data || response;
                if (articles && articles.length > 0) {
                    this.article = articles[0];
                } else {
                    this.error = 'Artikel tidak ditemukan atau telah dihapus.';
                }
                this.loading = false;
            },
            (err) => {
                console.error('Error loading article:', err);
                this.error = 'Gagal memuat artikel. Silakan coba lagi nanti.';
                this.loading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat artikel'
                });
            }
        );
    }

    goBack() {
        this.router.navigate(['/app/article/list']);
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    }

    formatContent(content: string): SafeHtml {
        if (!content) return '';
        return this.sanitizer.bypassSecurityTrustHtml(content);
    }

    getImageUrl(path: string | null | undefined): string {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
        if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
            return `http://localhost:8000${path.startsWith('/') ? '' : '/'}${path}`;
        }
        return `https://oscemediko.id${path.startsWith('/') ? '' : '/'}${path}`;
    }
}
